import { DEFERRED_BADGE_NUMBERS } from '../../../services/meditationAchievementsConstants';
import { MEDITATION_SESSION_TYPES } from '../../../services/meditationSessionTypeConstants';

export const HTTP_URL_RE = /^https?:\/\/.+/i;

/** Mongo ObjectId string — mirrors Backend Joi objectIdString. */
export const OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;

export const SESSION_TYPE_OPTIONS = [...MEDITATION_SESSION_TYPES];

export const isFestivalTriggerType = (triggerType) =>
  triggerType === 'special_date_range' || triggerType === 'special_date_set';

export const normalizeHinduFestivalIds = (raw) => {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const item of raw) {
    const id = String(item || '').trim();
    if (!OBJECT_ID_RE.test(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
};

/** ISO weekday labels — Backend: 1=Mon … 7=Sun */
export const WEEKDAY_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
];

export const isDeferredAchievement = (doc) => {
  if (!doc) return false;
  if (doc.deferred === true) return true;
  if (doc.trigger_type === 'deferred_program') return true;
  const n = Number(doc.badge_number);
  return DEFERRED_BADGE_NUMBERS.includes(n);
};

/** Fresh structured trigger fields for a trigger_type (wipe on type change). */
export const emptyTriggerFields = (triggerType = '') => {
  const base = {
    n: '1',
    min_seconds: '600',
    session_type: '',
    types: [...SESSION_TYPE_OPTIONS],
    month: '6',
    day: '21',
    key: '',
    hindu_festival_ids: [],
    weekday: '1',
    reason: '',
    windows: [{ start_hour: '5', end_hour: '8' }],
  };
  switch (triggerType) {
    case 'streak_days':
      return { ...base, n: '7' };
    case 'weekday_streak':
      return { ...base, n: '4', weekday: '1' };
    case 'cumulative_played_seconds':
      return { ...base, min_seconds: '3600' };
    case 'single_played_seconds':
      return { ...base, min_seconds: '600' };
    case 'session_type_first':
      return { ...base, session_type: 'pranayama' };
    case 'session_types_set':
      return { ...base, types: [...SESSION_TYPE_OPTIONS], n: '3' };
    case 'special_date_range':
      return { ...base, hindu_festival_ids: [] };
    case 'special_date_set':
      return { ...base, hindu_festival_ids: [] };
    case 'ritual_local_hour_count':
      return {
        ...base,
        n: '7',
        windows: [{ start_hour: '5', end_hour: '8' }],
      };
    case 'deferred_program':
      return { ...base, reason: '' };
    default:
      return base;
  }
};

/**
 * Option A: Admin never collects code / badge_number / icon_suggestion / order.
 */
export const emptyAchievementForm = () => ({
  name: '',
  sanskrit_name: '',
  description: '',
  category: '',
  rarity: '',
  logo_url: '',
  trigger_type: '',
  trigger: emptyTriggerFields(''),
  is_active: true,
  deferred: false,
  show_progress: true,
});

const strOr = (v, fallback = '') =>
  v != null && v !== '' ? String(v) : fallback;

export const triggerFieldsFromConfig = (triggerType, config) => {
  const fresh = emptyTriggerFields(triggerType);
  const cfg = config && typeof config === 'object' && !Array.isArray(config) ? config : {};

  switch (triggerType) {
    case 'session_count':
    case 'streak_days':
    case 'mood_before_session_count':
      return { ...fresh, n: strOr(cfg.n, fresh.n) };
    case 'weekday_streak':
      return {
        ...fresh,
        weekday: strOr(cfg.weekday, fresh.weekday),
        n: strOr(cfg.n, fresh.n),
      };
    case 'single_played_seconds':
    case 'cumulative_played_seconds':
      return { ...fresh, min_seconds: strOr(cfg.min_seconds, fresh.min_seconds) };
    case 'session_type_first':
      return { ...fresh, session_type: strOr(cfg.session_type, fresh.session_type) };
    case 'session_types_set': {
      const types = Array.isArray(cfg.types)
        ? cfg.types.filter((t) => SESSION_TYPE_OPTIONS.includes(t))
        : [...SESSION_TYPE_OPTIONS];
      return {
        ...fresh,
        types: types.length ? types : [...SESSION_TYPE_OPTIONS],
        n: strOr(cfg.n, fresh.n),
      };
    }
    case 'special_date':
      return {
        ...fresh,
        month: strOr(cfg.month, fresh.month),
        day: strOr(cfg.day, fresh.day),
        key: strOr(cfg.key, ''),
      };
    case 'special_date_range':
    case 'special_date_set':
      // Do not restore legacy trigger_config.key (catalogue label removed; Backend rejects key).
      return {
        ...fresh,
        hindu_festival_ids: normalizeHinduFestivalIds(cfg.hindu_festival_ids),
      };
    case 'ritual_local_hour_count': {
      const windows = Array.isArray(cfg.windows) && cfg.windows.length
        ? cfg.windows.map((w) => ({
            start_hour: strOr(w?.start_hour, ''),
            end_hour: strOr(w?.end_hour, ''),
          }))
        : [{ start_hour: '5', end_hour: '8' }];
      return { ...fresh, windows, n: strOr(cfg.n, fresh.n) };
    }
    case 'deferred_program':
      return { ...fresh, reason: strOr(cfg.reason, '') };
    default:
      return fresh;
  }
};

export const formFromAchievementDoc = (doc) => {
  if (!doc) return emptyAchievementForm();
  const trigger_type = doc.trigger_type || '';
  return {
    name: doc.name || '',
    sanskrit_name: doc.sanskrit_name || '',
    description: doc.description || '',
    category: doc.category || '',
    rarity: doc.rarity || '',
    logo_url: doc.logo_url || '',
    trigger_type,
    trigger: triggerFieldsFromConfig(trigger_type, doc.trigger_config),
    is_active: doc.is_active !== false,
    deferred: doc.deferred === true,
    show_progress: doc.show_progress !== false,
  };
};

const parsePositiveInt = (raw, label, errors, key = 'trigger') => {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) {
    errors[key] = `${label} must be an integer ≥ 1`;
    return null;
  }
  return n;
};

const isRealCalendarDay = (month, day) => {
  if (!Number.isInteger(month) || !Number.isInteger(day)) return false;
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= daysInMonth[month - 1];
};

/**
 * Build trigger_config object from structured fields. Only documented keys.
 * @returns {{ errors: Record<string,string>, trigger_config?: object }}
 */
export const buildTriggerConfig = (triggerType, trigger = {}) => {
  const errors = {};
  if (!triggerType) {
    errors.trigger_type = 'Trigger type is required';
    return { errors };
  }

  switch (triggerType) {
    case 'session_count':
    case 'streak_days':
    case 'mood_before_session_count': {
      const n = parsePositiveInt(trigger.n, 'Count (n)', errors);
      if (errors.trigger) return { errors };
      return { errors: {}, trigger_config: { n } };
    }
    case 'weekday_streak': {
      const weekday = Number(trigger.weekday);
      if (!Number.isInteger(weekday) || weekday < 1 || weekday > 7) {
        errors.trigger = 'Weekday must be Monday–Sunday (1–7)';
        return { errors };
      }
      const n = parsePositiveInt(trigger.n, 'Count (n)', errors);
      if (errors.trigger) return { errors };
      return { errors: {}, trigger_config: { weekday, n } };
    }
    case 'single_played_seconds':
    case 'cumulative_played_seconds': {
      const min_seconds = parsePositiveInt(trigger.min_seconds, 'Min seconds', errors);
      if (errors.trigger) return { errors };
      return { errors: {}, trigger_config: { min_seconds } };
    }
    case 'session_type_first': {
      const session_type = String(trigger.session_type || '').trim();
      if (!SESSION_TYPE_OPTIONS.includes(session_type)) {
        errors.trigger = 'Session type is required';
        return { errors };
      }
      return { errors: {}, trigger_config: { session_type } };
    }
    case 'session_types_set': {
      const types = Array.isArray(trigger.types)
        ? [...new Set(trigger.types.filter((t) => SESSION_TYPE_OPTIONS.includes(t)))]
        : [];
      if (!types.length) {
        errors.trigger = 'Select at least one session type';
        return { errors };
      }
      const n = parsePositiveInt(trigger.n, 'Count (n)', errors);
      if (errors.trigger) return { errors };
      if (n > types.length) {
        errors.trigger = `Count (n) cannot exceed selected types (${types.length})`;
        return { errors };
      }
      return { errors: {}, trigger_config: { types, n } };
    }
    case 'special_date': {
      const month = Number(trigger.month);
      const day = Number(trigger.day);
      if (!isRealCalendarDay(month, day)) {
        errors.trigger = 'Enter a valid calendar month and day';
        return { errors };
      }
      const trigger_config = { month, day };
      const key = String(trigger.key || '').trim();
      if (key) {
        if (key.length > 64) {
          errors.trigger = 'Optional key max 64 characters';
          return { errors };
        }
        trigger_config.key = key;
      }
      return { errors: {}, trigger_config };
    }
    case 'special_date_range':
    case 'special_date_set': {
      const hindu_festival_ids = normalizeHinduFestivalIds(trigger.hindu_festival_ids);
      if (!hindu_festival_ids.length) {
        errors.trigger = 'Select one Hindu Festival';
        return { errors };
      }
      if (hindu_festival_ids.length > 1) {
        errors.trigger =
          'Select only one Hindu Festival (this badge has multiple saved — pick one before saving)';
        return { errors };
      }
      // Never send trigger_config.key — Backend rejects it; dates resolve from IDs only.
      return { errors: {}, trigger_config: { hindu_festival_ids } };
    }
    case 'ritual_local_hour_count': {
      const windowsRaw = Array.isArray(trigger.windows) ? trigger.windows : [];
      if (!windowsRaw.length) {
        errors.trigger = 'Add at least one ritual hour window';
        return { errors };
      }
      const windows = [];
      for (let i = 0; i < windowsRaw.length; i++) {
        const start_hour = Number(windowsRaw[i].start_hour);
        const end_hour = Number(windowsRaw[i].end_hour);
        if (
          !Number.isInteger(start_hour) ||
          start_hour < 0 ||
          start_hour > 23 ||
          !Number.isInteger(end_hour) ||
          end_hour < 0 ||
          end_hour > 24
        ) {
          errors.trigger = `Window ${i + 1}: start 0–23, end 0–24`;
          return { errors };
        }
        if (start_hour === end_hour) {
          errors.trigger = `Window ${i + 1}: start and end hours must not be equal`;
          return { errors };
        }
        windows.push({ start_hour, end_hour });
      }
      const n = parsePositiveInt(trigger.n, 'Count (n)', errors);
      if (errors.trigger) return { errors };
      return { errors: {}, trigger_config: { windows, n } };
    }
    case 'deferred_program': {
      const reason = String(trigger.reason || '').trim();
      if (reason.length > 200) {
        errors.trigger = 'Reason max 200 characters';
        return { errors };
      }
      const trigger_config = reason ? { reason } : {};
      return { errors: {}, trigger_config };
    }
    default:
      errors.trigger_type = 'Unknown trigger type';
      return { errors };
  }
};

const buildSharedFields = (form) => {
  const errors = {};
  const name = String(form.name || '').trim();
  if (!name) errors.name = 'Name is required';
  const sanskrit = String(form.sanskrit_name || '').trim();
  if (!sanskrit) errors.sanskrit_name = 'Sanskrit / alternate name is required';
  const description = String(form.description || '').trim();
  if (!description) errors.description = 'Description is required';
  if (!form.category) errors.category = 'Category is required';
  if (!form.rarity) errors.rarity = 'Rarity is required';
  if (!form.trigger_type) errors.trigger_type = 'Trigger type is required';

  const logo = String(form.logo_url || '').trim();
  return { errors, name, sanskrit, description, logo };
};

/**
 * Client-side checks before API. Backend Joi remains authoritative.
 * @returns {{ errors: Record<string,string>, payload?: object }}
 */
export const buildCreatePayload = (form) => {
  const { errors, name, sanskrit, description, logo } = buildSharedFields(form);

  if (!logo) errors.logo_url = 'Logo is required';
  else if (!HTTP_URL_RE.test(logo)) errors.logo_url = 'Logo must be an HTTP or HTTPS URL';

  const built = buildTriggerConfig(form.trigger_type, form.trigger);
  Object.assign(errors, built.errors);

  const deferred =
    form.deferred === true || form.trigger_type === 'deferred_program';
  if (deferred && form.is_active === true) {
    errors.is_active = 'Deferred badges cannot be activated';
  }

  if (Object.keys(errors).length) return { errors };

  return {
    errors: {},
    payload: {
      name,
      sanskrit_name: sanskrit,
      description,
      category: form.category,
      rarity: form.rarity,
      logo_url: logo,
      trigger_type: form.trigger_type,
      trigger_config: built.trigger_config,
      is_active: deferred ? false : Boolean(form.is_active),
      deferred,
      show_progress: Boolean(form.show_progress),
    },
  };
};

/**
 * Update payload — never includes code, badge_number, icon_suggestion, or order.
 */
export const buildUpdatePayload = (achievementId, form, { forceInactive } = {}) => {
  const { errors, name, sanskrit, description, logo } = buildSharedFields(form);
  if (!achievementId) errors.achievement_id = 'Missing achievement id';

  if (logo && !HTTP_URL_RE.test(logo)) {
    errors.logo_url = 'Logo must be an HTTP or HTTPS URL';
  }

  const built = buildTriggerConfig(form.trigger_type, form.trigger);
  Object.assign(errors, built.errors);

  const deferred =
    forceInactive ||
    form.deferred === true ||
    form.trigger_type === 'deferred_program';
  const wantActive = forceInactive ? false : Boolean(form.is_active);
  if (deferred && wantActive) {
    errors.is_active = 'Deferred badges cannot be activated';
  }

  if (Object.keys(errors).length) return { errors };

  return {
    errors: {},
    payload: {
      achievement_id: String(achievementId),
      name,
      sanskrit_name: sanskrit,
      description,
      category: form.category,
      rarity: form.rarity,
      logo_url: logo || null,
      trigger_type: form.trigger_type,
      trigger_config: built.trigger_config,
      is_active: deferred ? false : wantActive,
      deferred,
      show_progress: Boolean(form.show_progress),
    },
  };
};
